/* =========================================================
   ZeonEra — front-end interactions (skin over Dawn)
   - on-scroll reveals
   - PDP gallery thumbnails
   - PDP variant option swatches -> Dawn product-form hidden input
   - decorative "light colour" glow swatches
   Add-to-cart + cart drawer are handled by Dawn's own JS.
   ========================================================= */
(function () {
  'use strict';

  /* ---- on-scroll reveal ---- */
  function mountReveal(root) {
    var els = (root || document).querySelectorAll('[data-reveal]:not(.in)');
    if (!('IntersectionObserver' in window)) {
      els.forEach(function (e) { e.classList.add('in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) {
          en.target.classList.add('in');
          io.unobserve(en.target);
        }
      });
    }, { threshold: 0.1 });
    els.forEach(function (e) { io.observe(e); });
  }

  /* ---- PDP ---- */
  function mountProduct(scope) {
    var pdp = (scope || document).querySelector('[data-zeon-pdp]');
    if (!pdp) return;

    var photo = pdp.querySelector('[data-pdp-photo]');
    var glow = pdp.querySelector('[data-pdp-glow]');
    var addText = pdp.getAttribute('data-add-text') || 'Add to bag';

    // gallery thumbnails
    pdp.querySelectorAll('[data-thumb]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        pdp.querySelectorAll('[data-thumb]').forEach(function (b) { b.classList.remove('on'); });
        btn.classList.add('on');
        if (photo && btn.dataset.thumb) photo.src = btn.dataset.thumb;
      });
    });

    // decorative glow swatches (no variant change)
    pdp.querySelectorAll('[data-glow]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        pdp.querySelectorAll('[data-glow]').forEach(function (b) {
          b.classList.remove('on');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('on');
        btn.setAttribute('aria-pressed', 'true');
        if (glow) glow.setAttribute('style', glowBG(btn.dataset.glow));
      });
    });

    // variant option swatches -> recompute variant
    var variantsEl = pdp.querySelector('[data-zeon-variants]');
    var variants = [];
    if (variantsEl) {
      try { variants = JSON.parse(variantsEl.textContent); } catch (e) { variants = []; }
    }
    var idInput = pdp.querySelector('[name="id"]');
    var submitBtn = pdp.querySelector('[data-add-btn]');
    var submitLabel = pdp.querySelector('[data-add-label]');
    var priceEl = pdp.querySelector('[data-pdp-price]');
    var discountEl = pdp.querySelector('[data-pdp-discount]');
    var stickyBar = pdp.querySelector('[data-pdp-sticky]');
    var stickyPrice = pdp.querySelector('[data-sticky-price]');
    var stickyAddBtn = pdp.querySelector('[data-sticky-add]');
    var stickyThumb = pdp.querySelector('[data-sticky-thumb]');
    var mobilePreviewQuery = window.matchMedia('(max-width: 860px)');
    var activeMobilePreview = null;
    var mobilePreviewTimer = null;

    function currentOptions() {
      var opts = [];
      pdp.querySelectorAll('[data-variant-option]:checked').forEach(function (input) {
        var idx = parseInt(input.getAttribute('data-option-index'), 10);
        opts[idx] = input.value;
      });
      return opts;
    }

    function findVariant(opts) {
      return variants.find(function (v) {
        return v.options.every(function (val, i) { return val === opts[i]; });
      });
    }

    function imageUrlWithWidth(src, width) {
      if (!src) return '';
      try {
        var url = new URL(src, window.location.href);
        url.searchParams.set('width', width);
        return url.toString();
      } catch (e) {
        return src;
      }
    }

    function updateFinishImages(opts) {
      pdp.querySelectorAll('[data-variant-option]').forEach(function (input) {
        var idx = parseInt(input.getAttribute('data-option-index'), 10);
        var candidateOptions = opts.slice();
        candidateOptions[idx] = input.value;
        var variant = findVariant(candidateOptions);
        if (!variant || !variant.featured_image) return;

        var label = input.closest('label');
        if (!label) return;
        var thumbnail = label.querySelector(':scope > img');
        var preview = label.querySelector('.finish-preview img');
        if (thumbnail) thumbnail.src = imageUrlWithWidth(variant.featured_image, 120);
        if (preview) preview.src = imageUrlWithWidth(variant.featured_image, 700);
      });
    }

    function hideMobileVariantPreview() {
      if (mobilePreviewTimer) {
        window.clearTimeout(mobilePreviewTimer);
        mobilePreviewTimer = null;
      }
      if (!activeMobilePreview) return;
      activeMobilePreview.classList.remove('is-visible');
      activeMobilePreview.setAttribute('aria-hidden', 'true');
      activeMobilePreview = null;
    }

    function showMobileVariantPreview(input) {
      if (!mobilePreviewQuery.matches || !variants.length) return;

      var row = input.closest('.swatch-row');
      var preview = row && row.querySelector('[data-mobile-variant-preview]');
      if (!preview) return;

      var opts = currentOptions();
      var idx = parseInt(input.getAttribute('data-option-index'), 10);
      opts[idx] = input.value;
      var variant = findVariant(opts);
      if (!variant || !variant.featured_image) return;

      if (activeMobilePreview && activeMobilePreview !== preview) {
        activeMobilePreview.classList.remove('is-visible');
        activeMobilePreview.setAttribute('aria-hidden', 'true');
      }

      var image = preview.querySelector('[data-mobile-variant-preview-image]');
      var label = preview.querySelector('[data-mobile-variant-preview-label]');
      if (image) image.src = imageUrlWithWidth(variant.featured_image, 700);
      if (label) label.textContent = input.value;

      activeMobilePreview = preview;
      preview.classList.add('is-visible');
      preview.setAttribute('aria-hidden', 'false');

      if (mobilePreviewTimer) window.clearTimeout(mobilePreviewTimer);
      mobilePreviewTimer = window.setTimeout(hideMobileVariantPreview, 1800);
    }

    function updateVariant() {
      if (!variants.length) return;
      var opts = currentOptions();
      updateFinishImages(opts);
      var v = findVariant(opts);
      if (!v) {
        if (submitBtn) {
          submitBtn.setAttribute('disabled', 'disabled');
          if (submitLabel) submitLabel.textContent = (window.variantStrings && window.variantStrings.unavailable) || 'Unavailable';
        }
        return;
      }
      if (idInput) idInput.value = v.id;

      var onSale = v.compare_price && v.compare_price > v.price;

      // price
      if (priceEl) {
        priceEl.innerHTML = (onSale ? '<s>' + v.compare_label + '</s>' : '') + v.price_label;
      }
      if (discountEl) {
        discountEl.hidden = !onSale;
        if (onSale) {
          discountEl.textContent = '-' + Math.round(((v.compare_price - v.price) * 100) / v.compare_price) + '%';
        }
      }

      // availability
      if (submitBtn) {
        if (v.available) {
          submitBtn.removeAttribute('disabled');
          submitBtn.removeAttribute('aria-disabled');
          if (submitLabel) submitLabel.textContent = addText + ' — ' + v.price_label;
        } else {
          submitBtn.setAttribute('disabled', 'disabled');
          if (submitLabel) submitLabel.textContent = (window.variantStrings && window.variantStrings.soldOut) || 'Sold out';
        }
      }

      // sticky bar mirror
      if (stickyPrice) stickyPrice.textContent = v.price_label;
      if (stickyAddBtn) {
        if (v.available) { stickyAddBtn.removeAttribute('disabled'); }
        else { stickyAddBtn.setAttribute('disabled', 'disabled'); }
      }

      // update URL for shareability without reload
      if (window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', url);
      }
    }

    pdp.querySelectorAll('[data-variant-option]').forEach(function (input) {
      input.addEventListener('click', function () {
        showMobileVariantPreview(input);
      });
      input.addEventListener('change', function () {
        // toggle visual state within the same option group
        var idx = input.getAttribute('data-option-index');
        pdp.querySelectorAll('[data-option-index="' + idx + '"]').forEach(function (sib) {
          var label = sib.closest('label');
          if (label) label.classList.toggle('on', sib.checked);
        });
        updateVariant();
      });
    });

    window.addEventListener('scroll', hideMobileVariantPreview, { passive: true });
    pdp.addEventListener('pointerdown', function (event) {
      if (activeMobilePreview && !event.target.closest('.finish')) hideMobileVariantPreview();
    });
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') hideMobileVariantPreview();
    });

    // Keep option thumbnails aligned with the initially selected variant too.
    updateFinishImages(currentOptions());

    // sticky add-to-cart bar: show once the main buy button scrolls away
    if (stickyBar) {
      if (stickyAddBtn && submitBtn) {
        stickyAddBtn.addEventListener('click', function () { submitBtn.click(); });
      }
      var anchor = pdp.querySelector('.pdp-actions');
      if (anchor && 'IntersectionObserver' in window) {
        var sio = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            var past = !en.isIntersecting && en.boundingClientRect.top < 0;
            stickyBar.classList.toggle('show', past);
            stickyBar.setAttribute('aria-hidden', past ? 'false' : 'true');
          });
        }, { threshold: 0 });
        sio.observe(anchor);
      }
    }
  }

  /* ---- product-card colour selectors ---- */
  function mountProductCardSwatches() {
    if (document.documentElement.dataset.cardSwatchesBound) return;
    document.documentElement.dataset.cardSwatchesBound = 'true';

    document.addEventListener('click', function (event) {
      var swatch = event.target.closest('[data-card-swatch]');
      if (!swatch) return;

      event.preventDefault();
      event.stopPropagation();

      var card = swatch.closest('[data-card-variant-selector]');
      if (!card) return;

      card.querySelectorAll('[data-card-swatch]').forEach(function (button) {
        var isSelected = button === swatch;
        button.classList.toggle('is-selected', isSelected);
        button.setAttribute('aria-checked', isSelected ? 'true' : 'false');
      });

      var image = card.querySelector('[data-card-product-image]');
      var src = swatch.getAttribute('data-card-image');
      if (image && src) {
        image.src = src;
        image.srcset = swatch.getAttribute('data-card-image-srcset') || '';
        image.alt = swatch.getAttribute('data-card-image-alt') || image.alt;
      }

      var price = card.querySelector('[data-card-price]');
      var currentPrice = card.querySelector('[data-card-current-price]');
      var comparePrice = card.querySelector('[data-card-compare-price]');
      var selectedPrice = swatch.getAttribute('data-card-price') || '';
      var selectedComparePrice = swatch.getAttribute('data-card-compare-price') || '';

      if (price && currentPrice && selectedPrice) {
        currentPrice.textContent = selectedPrice;
        price.setAttribute('aria-label', selectedComparePrice ? selectedComparePrice + ', now ' + selectedPrice : selectedPrice);
      }

      if (comparePrice) {
        comparePrice.textContent = selectedComparePrice;
        comparePrice.hidden = !selectedComparePrice;
      }

      var saleBadge = card.querySelector('[data-card-sale-badge]');
      if (saleBadge) {
        saleBadge.textContent = swatch.getAttribute('data-card-sale-label') || '';
        saleBadge.hidden = !saleBadge.textContent;
      }

      var variantId = swatch.getAttribute('data-card-variant-id');
      var productUrl = card.getAttribute('data-card-product-url');
      if (variantId && productUrl) {
        var separator = productUrl.indexOf('?') === -1 ? '?' : '&';
        var variantUrl = productUrl + separator + 'variant=' + encodeURIComponent(variantId);
        card.querySelectorAll('[data-card-product-link]').forEach(function (link) {
          link.href = variantUrl;
        });
      }

      var hoverImage = card.querySelector('[data-card-product-alt]');
      if (hoverImage && src) hoverImage.style.setProperty('--alt-img', 'url("' + src.replace(/"/g, '\\"') + '")');
    });
  }

  /* ---- shared launch offer refresh timer ---- */
  function mountSaleTimer(scope) {
    (scope || document).querySelectorAll('[data-pdp-timer]').forEach(function (el) {
      if (el.dataset.bound) return;
      el.dataset.bound = '1';
      var hrs = parseFloat(el.getAttribute('data-cycle-hours')) || 72;
      var durMs = hrs * 3600 * 1000;
      var anchor = Date.parse(el.getAttribute('data-cycle-anchor'));
      if (isNaN(anchor)) anchor = Date.UTC(2026, 6, 24, 0, 0, 0);
      var hEl = el.querySelector('[data-h]');
      var mEl = el.querySelector('[data-m]');
      var sEl = el.querySelector('[data-s]');
      function pad(n) { return n < 10 ? '0' + n : '' + n; }
      function tick() {
        var now = Date.now();
        var elapsedCycles = Math.floor((now - anchor) / durMs);
        var end = anchor + (elapsedCycles + 1) * durMs;
        var t = Math.max(0, end - now);
        var h = Math.floor(t / 3600000);
        var m = Math.floor((t % 3600000) / 60000);
        var s = Math.floor((t % 60000) / 1000);
        if (hEl) hEl.textContent = pad(h);
        if (mEl) mEl.textContent = pad(m);
        if (sEl) sEl.textContent = pad(s);
        el.setAttribute('aria-label', 'Launch offer ends in ' + h + ' hours, ' + m + ' minutes, and ' + s + ' seconds.');
      }
      tick();
      setInterval(tick, 1000);
    });
  }

  /* ---- mobile slide-in nav ---- */
  function mountMobileNav(scope) {
    var toggle = (scope || document).querySelector('[data-nav-toggle]');
    var mnav = document.querySelector('[data-mnav]');
    if (!toggle || !mnav || toggle.dataset.bound) return;
    toggle.dataset.bound = '1';

    function open() {
      mnav.hidden = false;
      requestAnimationFrame(function () { mnav.classList.add('open'); });
      toggle.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('mnav-lock');
    }
    function close() {
      mnav.classList.remove('open');
      toggle.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('mnav-lock');
      setTimeout(function () { if (!mnav.classList.contains('open')) mnav.hidden = true; }, 320);
    }
    toggle.addEventListener('click', open);
    mnav.querySelectorAll('[data-mnav-close]').forEach(function (el) { el.addEventListener('click', close); });
    mnav.querySelectorAll('[data-mnav-link]').forEach(function (el) { el.addEventListener('click', close); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && mnav.classList.contains('open')) close(); });
  }

  /* ---- card quick-add -> Dawn cart drawer ---- */
  function mountQuickAdd(scope) {
    (scope || document).querySelectorAll('[data-quick-add]').forEach(function (btn) {
      if (btn.dataset.bound) return;
      btn.dataset.bound = '1';
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var id = btn.getAttribute('data-variant-id');
        if (!id || btn.classList.contains('loading')) return;
        var label = btn.querySelector('.quick-add-label');
        var spin = btn.querySelector('.quick-add-spinner');
        var original = label ? label.textContent : '';
        btn.classList.add('loading');
        if (spin) spin.hidden = false;

        var cartDrawer = document.querySelector('cart-drawer');
        var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        var body = { items: [{ id: Number(id), quantity: 1 }] };
        if (cartDrawer) { body.sections = ['cart-drawer', 'cart-icon-bubble']; body.sections_url = window.location.pathname; }

        fetch(root + 'cart/add', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify(body)
        }).then(function (r) { return r.json(); }).then(function (res) {
          if (res.status) { window.location.href = root + 'cart'; return; }
          if (cartDrawer && res.sections && typeof cartDrawer.renderContents === 'function') {
            cartDrawer.renderContents(res);
          } else {
            window.location.href = root + 'cart';
            return;
          }
          btn.classList.add('added');
          if (label) label.textContent = 'Added ✓';
          setTimeout(function () { btn.classList.remove('added'); if (label) label.textContent = original; }, 1800);
        }).catch(function () {
          window.location.href = root + 'cart';
        }).then(function () {
          btn.classList.remove('loading');
          if (spin) spin.hidden = true;
        });
      });
    });
  }

  function glowBG(c) {
    return 'background: radial-gradient(56% 52% at 50% 45%, ' + c + '3d, ' + c + '14 46%, transparent 70%);';
  }

  /* ---- hero carousel ---- */
  function mountHeroSlider(scope) {
    (scope || document).querySelectorAll('[data-hero-slider]').forEach(function (slider) {
      var slides = slider.querySelectorAll('[data-slide]');
      var dots = slider.querySelectorAll('[data-hero-dot]');
      if (slides.length < 2) return;
      var idx = 0;

      function show(n) {
        idx = (n + slides.length) % slides.length;
        slides.forEach(function (s, k) { s.classList.toggle('on', k === idx); });
        dots.forEach(function (d, k) { d.classList.toggle('on', k === idx); });
      }

      var prev = slider.querySelector('[data-hero-prev]');
      var next = slider.querySelector('[data-hero-next]');
      if (prev) prev.addEventListener('click', function (e) { e.preventDefault(); show(idx - 1); });
      if (next) next.addEventListener('click', function (e) { e.preventDefault(); show(idx + 1); });
      dots.forEach(function (d, k) {
        d.addEventListener('click', function (e) { e.preventDefault(); show(k); });
      });

      show(0);
    });
  }

  /* ---- PDP image slider dots (mobile) ---- */
  function mountGallery(scope) {
    (scope || document).querySelectorAll('[data-pdp-gallery]').forEach(function (gallery) {
      if (gallery.dataset.bound) return;
      var wall = gallery.querySelector('[data-pdp-wall]');
      var dots = gallery.querySelectorAll('[data-pdp-dot]');
      if (!wall || dots.length < 2) return;
      gallery.dataset.bound = '1';
      var cells = wall.querySelectorAll('.pdp-wall-cell');

      function setActive(idx) {
        dots.forEach(function (d, k) { d.classList.toggle('on', k === idx); });
      }

      // Reflect the swiped position in the dots.
      var raf;
      wall.addEventListener('scroll', function () {
        if (raf) return;
        raf = window.requestAnimationFrame(function () {
          raf = null;
          var idx = Math.round(wall.scrollLeft / wall.clientWidth);
          setActive(Math.max(0, Math.min(cells.length - 1, idx)));
        });
      });

      // Tap a dot to jump to that image.
      dots.forEach(function (d, k) {
        d.addEventListener('click', function () {
          if (cells[k]) wall.scrollTo({ left: k * wall.clientWidth, behavior: 'smooth' });
        });
      });
    });
  }

  function mountGalleryViewToggle(scope) {
    (scope || document).querySelectorAll('[data-pdp-gallery]').forEach(function (gallery) {
      if (gallery.dataset.viewToggleBound) return;
      var wall = gallery.querySelector('[data-pdp-wall]');
      var buttons = gallery.querySelectorAll('[data-pdp-gallery-view]');
      if (!wall || !buttons.length) return;
      gallery.dataset.viewToggleBound = '1';

      buttons.forEach(function (button) {
        button.addEventListener('click', function () {
          if (gallery.dataset.viewTransitioning) return;
          var expanded = button.getAttribute('data-pdp-gallery-view') === 'single';
          var updateView = function () {
            wall.classList.toggle('pdp-wall--single', expanded);
            buttons.forEach(function (control) {
              var active = control === button;
              control.classList.toggle('is-active', active);
              control.setAttribute('aria-pressed', active ? 'true' : 'false');
            });
          };
          var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

          if (document.startViewTransition && !reducedMotion) {
            gallery.dataset.viewTransitioning = '1';
            document.startViewTransition(updateView).finished.then(function () {
              delete gallery.dataset.viewTransitioning;
            }, function () {
              delete gallery.dataset.viewTransitioning;
            });
          } else {
            updateView();
            wall.classList.remove('pdp-wall--fallback-animating');
            void wall.offsetWidth;
            wall.classList.add('pdp-wall--fallback-animating');
            window.setTimeout(function () {
              wall.classList.remove('pdp-wall--fallback-animating');
            }, 500);
          }
        });
      });
    });
  }

  function mountMeasurementDialogs(scope) {
    (scope || document).querySelectorAll('[data-measurements-open]').forEach(function (opener) {
      if (opener.dataset.bound) return;
      var targetId = opener.getAttribute('data-measurements-target');
      var dialog = targetId ? document.getElementById(targetId) : null;
      if (!dialog || typeof dialog.showModal !== 'function') return;
      opener.dataset.bound = '1';

      opener.addEventListener('click', function () {
        if (!dialog.open) dialog.showModal();
      });

      dialog.querySelectorAll('[data-measurements-close]').forEach(function (closeButton) {
        closeButton.addEventListener('click', function () { dialog.close(); });
      });

      dialog.addEventListener('click', function (event) {
        if (event.target === dialog) dialog.close();
      });
    });
  }

  /* ---- generic horizontal carousel (product rows) ---- */
  function mountZxCarousel(scope) {
    (scope || document).querySelectorAll('[data-zx-carousel]').forEach(function (sl) {
      var vp = sl.querySelector('[data-zx-viewport]');
      if (!vp || vp.dataset.bound) return;
      vp.dataset.bound = '1';
      var prev = sl.querySelector('[data-zx-prev]');
      var next = sl.querySelector('[data-zx-next]');

      function page(dir) {
        vp.scrollBy({ left: dir * vp.clientWidth * 0.9, behavior: 'smooth' });
        window.setTimeout(update, 450);
      }
      function update() {
        var cards = vp.querySelectorAll('.review');
        var viewportRect = vp.getBoundingClientRect();
        var firstCard = cards[0];
        var lastCard = cards[cards.length - 1];
        var noOverflow = !firstCard || !lastCard || vp.scrollWidth <= vp.clientWidth + 4;
        var atStart = noOverflow || firstCard.getBoundingClientRect().left >= viewportRect.left - 8;
        var atEnd = noOverflow || lastCard.getBoundingClientRect().right <= viewportRect.right + 8;

        if (prev) prev.hidden = atStart;
        if (next) next.hidden = atEnd;
      }
      if (prev) prev.addEventListener('click', function () { page(-1); });
      if (next) next.addEventListener('click', function () { page(1); });
      vp.addEventListener('scroll', update, { passive: true });
      vp.addEventListener('scrollend', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* ---- reviews carousel ---- */
  function mountReviewsSlider(scope) {
    (scope || document).querySelectorAll('[data-reviews-slider]').forEach(function (sl) {
      var vp = sl.querySelector('[data-reviews-viewport]');
      if (!vp || vp.dataset.bound) return;
      vp.dataset.bound = '1';
      var prev = sl.querySelector('[data-rev-prev]');
      var next = sl.querySelector('[data-rev-next]');

      function page(dir) {
        vp.scrollBy({ left: dir * vp.clientWidth * 0.9, behavior: 'smooth' });
      }
      function update() {
        var noOverflow = vp.scrollWidth <= vp.clientWidth + 4;
        var atStart = vp.scrollLeft <= 2;
        var atEnd = vp.scrollLeft >= vp.scrollWidth - vp.clientWidth - 2;
        if (prev) prev.hidden = noOverflow || atStart;
        if (next) next.hidden = noOverflow || atEnd;
      }
      if (prev) prev.addEventListener('click', function () { page(-1); });
      if (next) next.addEventListener('click', function () { page(1); });
      vp.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* ---- product navigation transition ---- */
  function mountPageTransition() {
    var transition = document.getElementById('zeon-page-transition');
    if (!transition || transition.dataset.bound) return;
    transition.dataset.bound = '1';

    // Warm likely destinations before the click so the cover screen is
    // mostly waiting on an already-requested document.
    var warmedDestinations = {};
    function getTransitionDestination(link) {
      if (!link || !link.href) return null;
      if (link.target && link.target !== '_self') return null;
      if (link.matches('button, .btn, .button, [role="button"], [download], [data-no-transition]')) return null;

      var destination;
      try {
        destination = new URL(link.href, window.location.href);
      } catch (error) {
        return null;
      }

      if (destination.origin !== window.location.origin) return null;
      if (
        destination.pathname === window.location.pathname &&
        destination.search === window.location.search
      ) return null;

      return destination;
    }

    function warmProductDestination(link) {
      var destination = getTransitionDestination(link);
      if (!destination || warmedDestinations[destination.href]) return;

      warmedDestinations[destination.href] = true;
      var prefetch = document.createElement('link');
      prefetch.rel = 'prefetch';
      prefetch.as = 'document';
      prefetch.href = destination.href;
      prefetch.fetchPriority = 'low';
      document.head.appendChild(prefetch);
    }

    function finishTransition() {
      document.documentElement.classList.remove('zeon-transitioning');
      transition.classList.remove('is-active');
      try {
        window.sessionStorage.removeItem('zeon-product-transition');
      } catch (error) {}
    }

    // A page restored from the back/forward cache is already complete.
    // A normal product navigation must remain covered until window.load.
    window.addEventListener('pageshow', function (event) {
      if (event.persisted) finishTransition();
    });

    if (document.documentElement.classList.contains('zeon-transitioning')) {
      var fadeStarted = false;
      var releaseTransition = function () {
        if (fadeStarted) return;
        fadeStarted = true;

        // Let the browser paint the ready destination behind the opaque
        // screen, then begin the visual handoff.
        window.requestAnimationFrame(function () {
          window.requestAnimationFrame(function () {
            window.setTimeout(finishTransition, 140);
          });
        });
      };

      var checkVisualReadiness = function () {
        if (document.readyState === 'loading') return;

        var images = Array.prototype.slice.call(document.images);
        if (!images.length) {
          releaseTransition();
          return;
        }

        var loadedImages = images.filter(function (image) {
          return image.complete;
        }).length;
        var visualProgress = loadedImages / images.length;

        // Begin the fade once the DOM is ready and most visual content is in.
        if (visualProgress >= 0.4 || document.readyState === 'complete') {
          releaseTransition();
        }
      };

      Array.prototype.forEach.call(document.images, function (image) {
        image.addEventListener('load', checkVisualReadiness, { once: true });
        image.addEventListener('error', checkVisualReadiness, { once: true });
      });

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', checkVisualReadiness, { once: true });
      } else {
        checkVisualReadiness();
      }

    }

    document.addEventListener('pointerenter', function (event) {
      var link = event.target.closest('a');
      if (link) warmProductDestination(link);
    }, true);

    document.addEventListener('focusin', function (event) {
      var link = event.target.closest('a');
      if (link) warmProductDestination(link);
    });

    document.addEventListener('click', function (event) {
      var link = event.target.closest('a');
      if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      var destination = getTransitionDestination(link);
      if (!destination) return;

      event.preventDefault();
      warmProductDestination(link);
      transition.classList.add('is-active');
      try {
        window.sessionStorage.setItem('zeon-product-transition', '1');
      } catch (error) {}
      window.setTimeout(function () {
        window.location.assign(destination.href);
      }, 350);
    }, true);
  }

  /* ---- boot ---- */
  function boot() {
    mountReveal(document);
    mountProduct(document);
    mountProductCardSwatches();
    mountGallery(document);
    mountGalleryViewToggle(document);
    mountMeasurementDialogs(document);
    mountHeroSlider(document);
    mountReviewsSlider(document);
    mountZxCarousel(document);
    mountSaleTimer(document);
    mountMobileNav(document);
    mountQuickAdd(document);
    mountPageTransition();
    // Fail-safe: never leave reveal content hidden/unclickable if the
    // IntersectionObserver doesn't fire for any reason.
    setTimeout(function () {
      document.querySelectorAll('[data-reveal]:not(.in)').forEach(function (e) {
        e.classList.add('in');
      });
    }, 1200);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: re-run on section load
  document.addEventListener('shopify:section:load', function (e) {
    mountReveal(e.target);
    mountProduct(e.target);
    mountGallery(e.target);
    mountGalleryViewToggle(e.target);
    mountMeasurementDialogs(e.target);
    mountHeroSlider(e.target);
    mountReviewsSlider(e.target);
    mountZxCarousel(e.target);
    mountSaleTimer(e.target);
    mountMobileNav(e.target);
    mountQuickAdd(e.target);
  });
})();
